import { Isolated } from './Bdi';
import type { ScheduleCard as ScheduleCardData } from '@pa/core';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { Button } from './Button';

/**
 * A calendar answer as a card, not prose: day headers in the person's language, one row per
 * event (start–end in tabular Western numerals, then the title), attendees only when asked,
 * and one footnote line for anything that needs care. Free slots use the same card with a
 * "Use this" affordance that sends the slot into the conversation (the usual approval flow
 * follows when it creates or moves an event). Works in RTL: times stay LTR and tabular.
 */
export function ScheduleCard({
  card,
  onUse,
}: {
  card: ScheduleCardData;
  onUse?: ((text: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const slots = card.kind === 'slots';
  return (
    <section
      aria-label={slots ? t('schedule.slotsLabel') : t('schedule.label')}
      data-card={card.card_id}
      className="rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-ink-2">
        <BrandMark size={16} />
        <span dir="auto">
          {slots ? t('schedule.slotsLabel') : t('schedule.label')}
          {card.range.label ? ` · ${card.range.label}` : ''}
        </span>
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {card.days.map((day) => (
          <div key={day.date}>
            <h4 className="text-sm font-semibold text-ink" dir="auto">
              {day.label}
            </h4>
            <ul className="mt-1 flex flex-col divide-y divide-line">
              {day.items.map((item) => (
                <li key={item.id} className="flex items-start gap-3 py-1.5 text-sm">
                  <span
                    dir="ltr"
                    className="tabular w-[7.5rem] shrink-0 text-ink-2 [unicode-bidi:isolate]"
                  >
                    {item.time}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-ink" dir="auto">
                      {item.title}
                    </span>
                    {item.attendees?.length ? (
                      <span className="block text-xs text-ink-2" dir="auto">
                        <Isolated text={item.attendees.join(', ')} />
                      </span>
                    ) : null}
                  </span>
                  {slots && item.use_text && onUse ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="md"
                      className="shrink-0"
                      onClick={() => onUse(item.use_text ?? '')}
                    >
                      {t('schedule.useThis')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {card.footnote ? (
        <p className="mt-3 border-t border-line pt-2 text-xs text-ink-2" dir="auto">
          {card.footnote}
        </p>
      ) : null}
    </section>
  );
}
